import { isNotEmptyTrimmedStringValidator } from '@currency-one/validators'
import { t9n } from 't9n/t9n'

import { CompetitionPayload } from 'models/endpoints-payload'
import { FormElements } from '../models/form-elements'

import { getApiErrorMessage, getGenericErrorMessage, isApiError } from 'utils/error-message'
import { modalHandle } from 'utils/dialog'

import competitionSubscribe from 'services/competition-subscribe'
import { checkEmailAvailability } from 'services/check-email-availability'

import {
  checkValidity,
  consentValidator,
  emailValidator,
  getBooleanField,
  getStringField,
  resetAllErrorMSG,
  setErrorMSG,
  setInputErrorMSG,
} from './form-handling'
import { handleNewsletterSubscription } from './newsletter'

type FormContainer = Document | HTMLElement
type RateValidator = (value: string, selector: string, formContainer: FormContainer) => string | null

export const getElementsForm = (): FormElements | null => {
  const $formContainerParent = document.querySelector<HTMLDivElement>('[data-collect-form]')
  if (!$formContainerParent) {
    console.warn('Form container not found')
    return null
  }

  const formElements = {
    $formContainerParent,
    $emailField: $formContainerParent.querySelector<HTMLInputElement>('[data-email-field]'),
    $errorEmailField: $formContainerParent.querySelector<HTMLSpanElement>('[data-error-field]'),
    $inputElementHolder: $formContainerParent.querySelector<HTMLDivElement>('[data-input-holder]'),
    $endView: $formContainerParent.querySelector<HTMLDivElement>('[data-end-view]'),
    $sendError: $formContainerParent.querySelector<HTMLParagraphElement>('[data-error-server]'),
    $eventInputElement: $formContainerParent.querySelector<HTMLInputElement>('[data-competition]'),
    $answerTextareaField: $formContainerParent.querySelector<HTMLTextAreaElement>('[data-answer]'),
    $errorAnswerField: $formContainerParent.querySelector<HTMLSpanElement>('[data-error-field-answer]'),
    $checkboxElement: $formContainerParent.querySelector<HTMLInputElement>('[data-checkbox-field="terms"]'),
    $submitFormBtn: $formContainerParent.querySelector<HTMLButtonElement>('[data-submit-form]'),
    $newsletterRodo: $formContainerParent.querySelector<HTMLInputElement>('[data-newsletter-rodo]'),
    $promotionCheckbox: $formContainerParent.querySelector<HTMLInputElement>('[data-checkbox-field="promotion"]'),
    $modal: document.querySelector<HTMLDialogElement>('[data-modal]'),
  }

  return formElements
}

// Function fetches data from form and create payload object.
export const getFormData = (formElements: FormElements, termsCheckboxSelector: string): Partial<CompetitionPayload> => {
  return {
    email: getStringField('[data-email-field]', emailValidator, formElements.$formContainerParent),
    event: formElements.$eventInputElement.value || undefined,
    answer: getStringField('[data-answer]', stringValidator, formElements.$formContainerParent) || '',
    date: new Date().toISOString(),
    terms_and_conditions_accepted: getBooleanField(
      termsCheckboxSelector,
      consentValidator,
      formElements.$formContainerParent,
    ),
  }
}

// Validator for rate inputs field
const rateValidator = (value: string, selector: string, formContainer: FormContainer = document): string | null => {
  const isValid = [!value ? t9n('formValidation.requireAllFields') : '']
  return checkValidity(isValid, selector, formContainer) ? value : null
}

const getRate = (
  selector: string,
  validator: RateValidator,
  formContainer: FormContainer = document,
): string | null => {
  const $inputs = formContainer.querySelectorAll<HTMLInputElement>(selector)
  const $numbers: Array<string> = []
  let isComplete = $inputs.length > 0

  $inputs.forEach(($input, key) => {
    const value = String($input.value).trim()
    if (!validator(value, selector, formContainer)) isComplete = false
    $numbers.push(key === 0 ? `${value},` : value)
  })

  return isComplete ? $numbers.join('') : null
}

const prepareAnswer = (formElements: FormElements): boolean => {
  const rate = getRate('[data-rate]', rateValidator, formElements.$formContainerParent)
  formElements.$answerTextareaField.value = rate ?? ''

  return !!rate
}

const handleRatesInput = (selector: string, formContainer: FormContainer = document): void => {
  const $rates = formContainer.querySelectorAll<HTMLInputElement>(selector)
  $rates.forEach(($rate, key) => {
    $rate.addEventListener('input', () => {
      $rate.value = $rate.value.replace(/[^0-9]/g, '')
      if (key + 1 < $rates.length && $rate.value) $rates[key + 1].focus()
      if (key - 1 >= 0 && $rate.value === '') $rates[key - 1].focus()
    })
  })
}

const stringValidator = (value: string, selector: string, formContainer: FormContainer = document): string | null => {
  const isValid = [!isNotEmptyTrimmedStringValidator(value) ? t9n('formValidation.emptyField') : '']
  return checkValidity(isValid, selector, formContainer) ? value : null
}

const validateCheckbox = (
  checkbox: HTMLInputElement | null | undefined,
  selector: string,
  formContainer: HTMLElement,
  isRequired: boolean = true,
): boolean => {
  if (!checkbox) return !isRequired

  if (!isRequired) return true

  return consentValidator(checkbox.checked, selector, formContainer)
}

export const checkIfCompetitionPayloadComplete = (
  payload: Partial<CompetitionPayload>,
): payload is CompetitionPayload => {
  const REQUIRED_FIELDS: ReadonlyArray<keyof CompetitionPayload> = [
    'email',
    'event',
    'answer',
    'terms_and_conditions_accepted',
  ]

  const requiredFieldsValid = REQUIRED_FIELDS.every((field) => {
    const value = payload[field]

    if (value == null) return false

    switch (field) {
      case 'email':
      case 'event':
        return typeof value === 'string' && value.trim() !== ''
      case 'answer':
        return typeof value === 'string' // answer can be empty string
      case 'terms_and_conditions_accepted':
        return value === true
      default:
        return true // For any other fields, just check for non-null/undefined
    }
  })

  return requiredFieldsValid
}

const isFormLocallyValid = (
  payload: Partial<CompetitionPayload>,
  formElements: FormElements,
  isNewsletterConsentRequired: boolean,
): payload is CompetitionPayload => {
  const isPayloadComplete = checkIfCompetitionPayloadComplete(payload)

  const isNewsletterConsentValid = validateCheckbox(
    formElements.$newsletterRodo,
    '[data-newsletter-rodo]',
    formElements.$formContainerParent,
    isNewsletterConsentRequired,
  )

  const isPromotionConsentValid = validateCheckbox(
    formElements.$promotionCheckbox,
    '[data-checkbox-field="promotion"]',
    formElements.$formContainerParent,
    false,
  )

  if (!isPayloadComplete || !isNewsletterConsentValid || !isPromotionConsentValid) {
    console.warn('Payload is not complete or newsletter consent is missing')
    return false
  }

  return true
}

const checkEmailExistence = async (email: string, formElements: FormElements): Promise<boolean | null> => {
  try {
    const emailResponse = await checkEmailAvailability({ email })

    if ('available' in emailResponse) {
      return !emailResponse.available
    }

    console.error('Email check returned non-200 status:', emailResponse.response.status)
    setErrorMSG('[data-error-server]', t9n('formValidation.serverUnknownError'), formElements.$formContainerParent)
    return null
  } catch (error: unknown) {
    if (isApiError(error)) {
      const errorMessage = getApiErrorMessage(error)
      console.error('API error during email existence check:', errorMessage)
      setErrorMSG('[data-error-server]', t9n('formValidation.serverUnknownError'), formElements.$formContainerParent)
    } else {
      const genericErrorMessage = getGenericErrorMessage(error)
      console.error('Unknown error during email existence check:', genericErrorMessage)
      setErrorMSG('[data-error-server]', t9n('formValidation.serverUnknownError'), formElements.$formContainerParent)
    }
    return null
  }
}

const handleSubmissionSuccess = (formElements: FormElements): void => {
  if (formElements.$modal) {
    formElements.$modal.showModal()
  } else {
    formElements.$inputElementHolder.hidden = true
    formElements.$endView.hidden = false
  }
  clearForm(formElements.$formContainerParent)
}

export const collectDataToSend = async (
  formElements: FormElements,
  isRate: boolean = false,
  isNewsletterConsentRequired: boolean = false,
  termsCheckboxSelector: string = '[data-checkbox-field="terms"]',
  requireRegisteredEmail: boolean = true,
): Promise<void> => {
  resetAllErrorMSG(formElements.$formContainerParent)

  const isRateComplete = !isRate || prepareAnswer(formElements)

  const payload = getFormData(formElements, termsCheckboxSelector)

  if (!isFormLocallyValid(payload, formElements, isNewsletterConsentRequired) || !isRateComplete) {
    return
  }

  if (requireRegisteredEmail) {
    const emailExists = await checkEmailExistence(payload.email, formElements)

    if (emailExists === null) {
      return
    }

    if (!emailExists) {
      console.error('Email does not exist in the database.')
      setInputErrorMSG('[data-email-field]', t9n('formValidation.emailUnregistered'), formElements.$formContainerParent)
      return
    }
  }

  if (formElements.$newsletterRodo?.checked) {
    try {
      const newsletterResponse = await handleNewsletterSubscription(formElements)

      if (!newsletterResponse) {
        console.error('Newsletter subscription failed (falsy response).')
      }
    } catch (err) {
      console.error('Newsletter subscription threw an error:', err)
    }
  }

  try {
    await competitionSubscribe(payload)
    handleSubmissionSuccess(formElements)
  } catch (error: unknown) {
    if (isApiError(error)) {
      const errorMessage = getApiErrorMessage(error)
      console.error('API error during competition subscription:', errorMessage)
      setErrorMSG('[data-error-server]', errorMessage, formElements.$formContainerParent)
    } else {
      const genericErrorMessage = getGenericErrorMessage(error)
      console.error('Unknown error during competition subscription:', genericErrorMessage)
      setErrorMSG('[data-error-server]', genericErrorMessage, formElements.$formContainerParent)
    }
  }
}

const clearForm = (formContainer: FormContainer = document): void => {
  const inputs = formContainer.querySelectorAll<HTMLInputElement>('input:not([data-competition])')
  const textareas = formContainer.querySelectorAll<HTMLTextAreaElement>('textarea')

  inputs.forEach((input) => {
    input.type !== 'checkbox' ? (input.value = '') : (input.checked = false)
  })

  textareas.forEach((textarea) => {
    textarea.value = ''
  })
}

export interface InitFormOptions {
  isNewsletterConsentRequired?: boolean
  termsCheckboxSelector?: string
  requireRegisteredEmail?: boolean
}

export const initForm = (options: InitFormOptions = {}): void => {
  const {
    isNewsletterConsentRequired = false,
    termsCheckboxSelector = '[data-checkbox-field="terms"]',
    requireRegisteredEmail = true,
  } = options

  const formElements = getElementsForm()

  if (!formElements) {
    console.error('Form elements could not be initialized.')
    return
  }

  const isRate = !!formElements.$formContainerParent.querySelector<HTMLInputElement>('[data-rate]')

  formElements.$submitFormBtn?.addEventListener('click', () =>
    collectDataToSend(formElements, isRate, isNewsletterConsentRequired, termsCheckboxSelector, requireRegisteredEmail),
  )

  if (isRate) handleRatesInput('[data-rate]', formElements.$formContainerParent)
  if (formElements.$modal) modalHandle(formElements.$modal)
}
